import { msg } from '@lingui/core/macro';

/**
 * Date utility functions for formatting and comparing dates
 */

/**
 * Checks if a date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Checks if a date is tomorrow
 */
export const isTomorrow = (date: Date): boolean => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear()
  );
};

/**
 * Checks if two dates are the same day
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

/**
 * Checks if a date is in the current month
 */
export const isCurrentMonth = (date: Date): boolean => {
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
};

/**
 * Formats a date to display format
 * Returns "Today" for today, "Tomorrow" for tomorrow, or localized date format for other dates
 *
 * @param date - The date to format
 * @param translate - Lingui translation function from useLingui()._
 * @param locale - Optional locale string (defaults to navigator.language or 'de-DE')
 * @returns Formatted date string
 */
export const formatDateDisplay = (
  date: Date,
  translate: (message: ReturnType<typeof msg>) => string,
  locale?: string
): string => {
  const effectiveLocale = locale || navigator.language || 'de-DE';

  if (isToday(date)) {
    return translate(msg`Today`);
  }

  if (isTomorrow(date)) {
    return translate(msg`Tomorrow`);
  }

  // Use localized date format (e.g., "Wed, Jan 13" in English)
  return date.toLocaleDateString(effectiveLocale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats time to display format using Intl localization
 * Will use 12-hour or 24-hour format based on user's locale
 *
 * @param date - The date to format
 * @param locale - Optional locale string (defaults to navigator.language or 'de-DE')
 * @returns Formatted time string
 */
export const formatTimeDisplay = (date: Date, locale?: string): string => {
  const effectiveLocale = locale || navigator.language || 'de-DE';
  return date.toLocaleTimeString(effectiveLocale, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Formats month and year for display (e.g., "January 2026")
 *
 * @param date - The date to format
 * @param locale - Optional locale string (defaults to navigator.language or 'de-DE')
 * @returns Formatted month and year string
 */
export const formatMonthYear = (date: Date, locale?: string): string => {
  const effectiveLocale = locale || navigator.language || 'de-DE';
  return new Intl.DateTimeFormat(effectiveLocale, {
    year: 'numeric',
    month: 'long',
  }).format(date);
};

/**
 * Formats date for HTML datetime attribute (YYYY-MM-DD)
 *
 * @param date - The date to format
 * @returns ISO date string (YYYY-MM-DD)
 */
export const formatDateForHTML = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Formats an ISO date string to localized date format
 * Useful for displaying dates from API responses (e.g., VTP firstRegistration)
 *
 * Uses UTC timezone to avoid date shifts when formatting dates with timezone offsets.
 * For example, "2014-09-01T00:00:00+02:00" will always display as 09/01/2014,
 * regardless of the user's local timezone.
 *
 * @param isoDateString - ISO date string (e.g., "2024-05-15T00:00:00.000Z" or "2024-05-15")
 * @param locale - Optional locale string (defaults to navigator.language or 'de-DE')
 * @returns Formatted date string or undefined if input is invalid
 */
export const formatISODateString = (isoDateString: string | undefined, locale?: string): string | undefined => {
  if (!isoDateString) return undefined;

  try {
    // Extract the date portion (YYYY-MM-DD) from the ISO string to avoid timezone conversion
    // This ensures dates like "2014-09-01T00:00:00+02:00" display as 09/01/2014
    // regardless of the user's timezone or the timezone offset in the string
    const dateMatch = isoDateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) {
      // Fallback for non-standard formats
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) return undefined;

      const effectiveLocale = locale || navigator.language || 'de-DE';
      return new Intl.DateTimeFormat(effectiveLocale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'UTC',
      }).format(date);
    }

    // Create a UTC date from the extracted date components
    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
    const day = parseInt(dateMatch[3], 10);

    // Create date using UTC to avoid timezone shifts
    const date = new Date(Date.UTC(year, month, day));
    if (isNaN(date.getTime())) return undefined;

    const effectiveLocale = locale || navigator.language || 'de-DE';
    return new Intl.DateTimeFormat(effectiveLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC',
    }).format(date);
  } catch {
    return undefined;
  }
};

/**
 * Creates a date key in YYYY-M-D format for caching/grouping by specific day
 *
 * @param date - The date to create a key for
 * @returns Date key string (YYYY-M-D)
 */
export const createDateKey = (date: Date): string => {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

/**
 * Creates a month key in YYYY-MM format for caching/grouping by month
 *
 * @param date - The date to create a month key for
 * @returns Month key string (YYYY-MM)
 */
export const createMonthKey = (date: Date): string => {
  return `${date.getFullYear()}-${date.getMonth()}`;
};

/**
 * Normalizes a date to midnight (00:00:00.000) for date-only comparisons
 *
 * @param date - The date to normalize
 * @returns New Date object at midnight
 */
export const normalizeToMidnight = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Gets the date for the previous month
 *
 * @param date - The reference date
 * @returns New Date object for the previous month
 */
export const getPreviousMonth = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setMonth(date.getMonth() - 1);
  return newDate;
};

/**
 * Gets the date for the next month
 *
 * @param date - The reference date
 * @returns New Date object for the next month
 */
export const getNextMonth = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setMonth(date.getMonth() + 1);
  return newDate;
};

/**
 * Gets month boundaries (first and last day) along with year and month values
 *
 * @param date - The date to get month boundaries for
 * @returns Object containing firstDay, lastDay, year, and month
 */
export const getMonthBoundaries = (date: Date): { firstDay: Date; lastDay: Date; year: number; month: number } => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  return { firstDay, lastDay, year, month };
};

/**
 * Gets the start and end of a month as ISO 8601 strings for API queries
 * Start: First day at 00:00:00.000
 * End: Last day at 23:59:59.999
 *
 * @param date - The date within the month
 * @returns Object with startDate and endDate as ISO strings
 */
export const getMonthDateRange = (date: Date): { startDate: string; endDate: string } => {
  const year = date.getFullYear();
  const month = date.getMonth();

  // Start of month: 1st day at 00:00:00
  const startDate = new Date(year, month, 1);
  startDate.setHours(0, 0, 0, 0);

  // End of month: last day at 23:59:59.999
  const endDate = new Date(year, month + 1, 0);
  endDate.setHours(23, 59, 59, 999);

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

/**
 * Gets the ISO weekday index (Monday = 0, Sunday = 6)
 * Converts JavaScript's Sunday-based index (0-6) to ISO 8601 Monday-based index
 *
 * @param date - The date to get the weekday index for
 * @returns ISO weekday index (0 = Monday, 6 = Sunday)
 */
export const getISOWeekdayIndex = (date: Date): number => {
  return date.getDay() === 0 ? 6 : date.getDay() - 1;
};

/**
 * Gets localized day names for the week starting with Monday
 *
 * @param locale - Optional locale string (defaults to navigator.language or 'de-DE')
 * @param format - 'short' for abbreviated (Mon, Tue) or 'long' for full names (Monday, Tuesday)
 * @returns Array of 7 localized day names starting with Monday
 */
export const getLocalizedDayNames = (locale?: string, format: 'short' | 'long' = 'short'): string[] => {
  const effectiveLocale = locale || navigator.language || 'de-DE';
  const formatter = new Intl.DateTimeFormat(effectiveLocale, { weekday: format });
  const firstDayOfWeek = new Date(2024, 0, 1); // Monday, Jan 1, 2024

  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(firstDayOfWeek);
    day.setDate(firstDayOfWeek.getDate() + i);
    return formatter.format(day);
  });
};

/**
 * Calendar day type for calendar grid generation
 */
export interface ICalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  sessionCount: number;
}

/**
 * Generates a 42-day calendar grid (6 weeks) for a given month
 * Includes trailing days from previous month and leading days from next month
 *
 * @param currentDate - The date within the month to generate the calendar for
 * @param getSessionCount - Function to get the number of sessions for a given date
 * @returns Array of 42 CalendarDay objects representing the calendar grid
 */
export const generateCalendarGrid = (currentDate: Date, getSessionCount: (date: Date) => number): ICalendarDay[] => {
  const { year, month, firstDay, lastDay } = getMonthBoundaries(currentDate);
  const today = normalizeToMidnight(new Date());

  // Start from Monday (ISO 8601)
  const startDay = getISOWeekdayIndex(firstDay);
  const daysInMonth = lastDay.getDate();
  const days: ICalendarDay[] = [];

  // Add days from previous month
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDay - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, prevMonthLastDay - i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: date.getTime() === today.getTime(),
      sessionCount: getSessionCount(date),
    });
  }

  // Add days from current month
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    days.push({
      date,
      isCurrentMonth: true,
      isToday: date.getTime() === today.getTime(),
      sessionCount: getSessionCount(date),
    });
  }

  // Add days from next month to complete the grid (42 days = 6 weeks)
  const remainingDays = 42 - days.length;
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: date.getTime() === today.getTime(),
      sessionCount: getSessionCount(date),
    });
  }

  return days;
};
